/**
 * The one way the app calls the backend: `api_gateway?service=…&action=…`.
 *
 * Before this (docs/TASKS.md C1), every service built its own URL and headers,
 * and components, hooks and stores called `fetch` directly — a change to how
 * a request authenticates was an edit in dozens of places. This module owns
 * the URL and the Authorization header, and nothing else.
 *
 * It deliberately does NOT parse responses or flatten errors. Callers keep
 * their own handling, because a 402's body is rendered verbatim as the upsell
 * and a 403 means "never" (ARCHITECTURE rule 12) — a client that swallowed
 * them would break the paid-gate path.
 */
import { GATEWAY_URL } from '../utils/apiConfig';
import { optionalAuthHeaders } from '../utils/optionalAuthHeaders';

/**
 * How a call authenticates. Every call site states it, because the right
 * answer differs and the wrong default is silent:
 * - `required` — the route needs a signed-in user; throws `NotSignedInError` without one.
 * - `optional` — open to visitors, but a signed-in account is treated differently.
 * - `none`     — public data; never sends a token.
 * - `{ token }` — the caller already holds the token it must send (it was
 *   handed one, e.g. the export's summary pass), so it is sent as given.
 */
export type GatewayAuth = 'required' | 'optional' | 'none' | { token: string };

export type GatewayParams = Record<string, string | number | boolean | null | undefined>;

export interface GatewayRequest {
  auth: GatewayAuth;
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  /** Extra query parameters after service and action; null and undefined are left out. */
  params?: GatewayParams;
  /** A plain value is sent as JSON; FormData, Blob and strings are sent as given. */
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

/** A `required` call was made with nobody signed in. */
export class NotSignedInError extends Error {
  constructor(message = 'Sign in to continue.') {
    super(message);
    this.name = 'NotSignedInError';
  }
}

/** `${GATEWAY_URL}?service=…&action=…&…`, each value URI-encoded, in the order given. */
export function gatewayUrl(service: string, action?: string, params: GatewayParams = {}): string {
  const pairs: Array<[string, string | number | boolean]> = [['service', service]];
  if (action) pairs.push(['action', action]);
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null) pairs.push([key, value]);
  }
  const query = pairs.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`).join('&');
  return `${GATEWAY_URL}?${query}`;
}

async function requiredAuthHeaders(): Promise<Record<string, string>> {
  const { auth } = await import('../firebase');
  const user = auth.currentUser;
  if (!user) throw new NotSignedInError();
  return { Authorization: `Bearer ${await user.getIdToken()}` };
}

async function authHeaders(mode: GatewayAuth): Promise<Record<string, string>> {
  if (typeof mode === 'object') return { Authorization: `Bearer ${mode.token}` };
  if (mode === 'required') return requiredAuthHeaders();
  if (mode === 'optional') return optionalAuthHeaders();
  return {};
}

function isRawBody(body: unknown): boolean {
  return (
    typeof body === 'string' ||
    (typeof FormData !== 'undefined' && body instanceof FormData) ||
    (typeof Blob !== 'undefined' && body instanceof Blob) ||
    (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams)
  );
}

/** Call a gateway route and return the raw `Response`. */
export async function gatewayFetch(service: string, action: string | undefined, request: GatewayRequest): Promise<Response> {
  const { auth, params, body, signal } = request;
  const headers: Record<string, string> = { ...(await authHeaders(auth)) };
  let payload: BodyInit | undefined;
  if (body !== undefined) {
    if (isRawBody(body)) {
      payload = body as BodyInit;
    } else {
      headers['Content-Type'] = 'application/json';
      payload = JSON.stringify(body);
    }
  }
  Object.assign(headers, request.headers);   // the caller's headers win
  const method = request.method ?? (body === undefined ? 'GET' : 'POST');
  return fetch(gatewayUrl(service, action, params), { method, headers, body: payload, signal });
}
